import React, { FC, useContext, useEffect, useState } from "react";
import Typhography from "../../shared/Typhography/Typhography";
import CustomButton from "../../shared/CustomButton/CustomButton";
import CustomTextInput from "../../shared/CustomInput/CustomTextInput";

import AddUserModal from "../../components/UserPage/AddUserModal/AddUserModal";
import CustomTable from "../../shared/CustomTable/CustomTable";
import {
  EnvelopeIcon,
  MagnifyingGlassIcon,
  PencilSimpleLineIcon,
  TrashIcon,
  UserIcon,
} from "@phosphor-icons/react";
import CustomTabsV3 from "../../shared/CustomTabsV3/CustomTabsV3";
import { Avatar } from "@chakra-ui/react";
import useAsyncTable from "../../hooks/useAsyncTable";
import { USER_TYPES } from "../../enums/user";
import { getLabelFromOptions } from "../../lib/DurationPicker/utils";
import {
  LEVELS_SPORT_OPTIONS,
  ROLES_SPORT_OPTIONS,
  USER_STATE_OPTIONS,
  USER_TYPE_OPTIONS,
} from "../../constants/config";
import { CustomBadge } from "../../shared/CustomBadge/CustomBadge";
import { IUser2 } from "../../models/user copy";
import style from "./UsersPage.module.scss";
import classNames from "classnames";
import { ITeam } from "../../models/Team";
import { TypeSpaceIcon } from "../../shared/Icons/Icons";
import { ISpace } from "../../models/Space";
import toast from "react-hot-toast";
import { useEndpoints } from "../../hooks/useEndpoints";
import CustomPaginationTable from "../../shared/CustomPaginationTable/CustomPaginationTable";
import { AuthContext } from "../../providers/authProvider";
import { useNavigate } from "react-router-dom";
import { ShowByRole } from "../../shared/ShowByRole";
import { EndpointWithPagination } from "../../typings/Pagination";
import dayjs from "dayjs";
import { formatFullName } from "../../utils/functions";

function summarizeArray<T>(arr: T[]) {
  if (arr.length <= 3) {
    return {
      items: arr,
      remainingCount: 0,
    };
  }

  return {
    items: arr.slice(0, 2),
    remainingCount: arr.length - 2,
  };
}

interface IUsersPage {}
const tabs = [
  {
    label: "Tutti",
    value: "all",
    icon: <UserIcon />,
  },
  {
    label: "Atleti",
    value: USER_TYPES.ATHLETE,
  },
  {
    label: "Preparatori atletici",
    value: USER_TYPES.COACH,
  },
  {
    label: "Amministratori",
    value: USER_TYPES.ADMINISTRATOR,
  },
];
const UsersPage: FC<IUsersPage> = () => {
  const [currentTab, setCurrentTab] = useState<USER_TYPES | "all">("all");
  const [isOpen, setIsOpen] = useState<true | undefined>();
  const { isAdmin, isDirector } = useContext(AuthContext);

  const navigate = useNavigate();
  const { deleteUser, refreshUserToken } = useEndpoints();
  const {
    data,
    isLoading,
    searchValue,
    updatePagination,
    pagination,
    updateSearchValue,
    fetch,
    updateParam,
  } = useAsyncTable<
    "getUsers",
    { type?: USER_TYPES; populate: string },
    EndpointWithPagination<IUser2<string, [ISpace]>>
  >("getUsers", undefined, {
    populate:
      "image,teams.name,teams.level,spaceId.type,spaceId.name,spaceId.gender,teams:spaceId",
  });
  const onClose = () => {
    setIsOpen(undefined);
    fetch();
  };
  const onOpen = () => {
    setIsOpen(true);
  };

  useEffect(() => {
    if (currentTab === "all") {
      updateParam("type", undefined);
    } else {
      updateParam("type", currentTab);
    }
  }, [currentTab]);

  const onDeleteUserClick = async (user: IUser2<string, [ISpace]>) => {
    const fullName = formatFullName(user.firstName, user.lastName);
    const id = toast.loading(
      `Rimozione ${formatFullName(user.firstName, user.lastName)} in corso...`,
    );
    try {
      await deleteUser(user._id);
      toast.success(`${fullName} rimosso`, { id });
      fetch();
    } catch (error) {
      toast.error("Qualcosa è andato storto", { id });
    }
  };

  const onRefreshToken = async (record: any) => {
    const id = toast.loading(`Caricamento...`);
    try {
      await refreshUserToken(record._id);
      toast.success(`Email mandata con successo`, { id });
    } catch (error) {
      console.error("error refresh token:", error);
      toast.error(`Errore imprevisto`, { id });
    }
  };
  return (
    <>
      <div className="container-page">
        {/* BUG-A11Y-013 fix: heading semantic h1 (era default <p>) */}
        <Typhography size="bold-24" type="h1">Utenti</Typhography>
        <Typhography size="regular-14" className="mt-2">
          Gestisci tutte le informazioni e i ruoli degli utenti
        </Typhography>
        <div className="w-100 d-flex align-items-center justify-content-between mt-3">
          <div className="d-flex align-items-center gap-3">
            <CustomTextInput
              value={searchValue}
              placeholder="Cerca"
              rightAddon={<MagnifyingGlassIcon />}
              onChange={updateSearchValue}
            />
          </div>
          <ShowByRole roles={["isAdmin", "isDirector"]}>
            <CustomButton
              iconPosition="left"
              icon={"plus"}
              color="secondary"
              onClick={() => {
                onOpen();
              }}
            >
              {currentTab === USER_TYPES.COACH
                ? "Aggiungi coach"
                : currentTab === USER_TYPES.ADMINISTRATOR
                  ? "Aggiungi amministratore"
                  : "Aggiungi atleta"}
            </CustomButton>
          </ShowByRole>
        </div>
        <CustomTabsV3
          className="mt-3"
          defaultValue={"Atleti"}
          tabs={tabs}
          value={currentTab}
          onValueChange={(e) => {
            setCurrentTab(e.value as USER_TYPES);
          }}
        ></CustomTabsV3>
        <div className="mt-2 mb-2 d-flex flex-column  flex-1 overflow-hidden">
          <CustomTable
            isLoading={isLoading}
            className="flex-1 overflow-auto"
            disabledRecord={(user) => !user.isVerified}
            optionsRecord={
              isAdmin || isDirector
                ? [
                    {
                      show: (user) => user.isVerified,
                      label: "Modifica utente",
                      icon: <PencilSimpleLineIcon size={12} />,
                      onclick: (item) => {
                        setIsOpen(item as any);
                      },
                    },
                    {
                      show: (user) => !user.isVerified,
                      label: "Rimanda email invito",
                      icon: <EnvelopeIcon size={12} />,
                      onclick: onRefreshToken,
                    },
                    {
                      color: "red",
                      icon: <TrashIcon size={12} />,
                      label: "Cancella utente",
                      onclick: (item) => {
                        onDeleteUserClick(item);
                      },
                    },
                  ]
                : undefined
            }
            data={data?.docs}
            headers={["Nome", "Ruolo", "Team", "Attivo", "Ultima attività"]}
            fields={{
              "Ultima attività": {
                key: "state",
                customValue: () => "-",
              },
              Attivo: {
                key: "state",
                customValue: (value: IUser2["state"]) =>
                  getLabelFromOptions(USER_STATE_OPTIONS, value) || "",
              },
              Nome: {
                key: "firstName",
                customComponent: (_, record) => {
                  const fullName = formatFullName(record.firstName, record.lastName);
                  const isAthlete = record.type === USER_TYPES.ATHLETE;
                  const handleClick = isAthlete
                    ? () => navigate(`/dashboard/users/${record._id}`)
                    : undefined;
                  return (
                    <div
                      className="d-flex align-items-center gap-2"
                      style={isAthlete ? { cursor: "pointer" } : undefined}
                      onClick={handleClick}
                    >
                      <Avatar.Root
                        className={classNames(style.userContainer, {
                          [style.imageUserContainer]: !!record.image,
                        })}
                      >
                        <Avatar.Fallback name={fullName} />
                        <Avatar.Image
                          src={
                            record.image
                              ? `${process.env.REACT_APP_BASE_URL_IMAGE}${record.image.image}`
                              : undefined
                          }
                        />
                      </Avatar.Root>
                      <div className="d-flex flex-column gap-2">
                        <Typhography size="bold-14">{fullName}</Typhography>
                        <Typhography
                          size="medium-12"
                          className={style.labelEmail}
                        >
                          {record.email}
                        </Typhography>
                      </div>
                    </div>
                  );
                },
              },
              Ruolo: {
                key: "type",
                customComponent: (_, record: IUser2<string, [ISpace]>) => {
                  const isAdmin = record.type === USER_TYPES.ADMINISTRATOR;
                  const isCoach = record.type === USER_TYPES.COACH;
                  return (
                    <div
                      className={classNames(
                        "d-flex align-items-center gap-2",
                        style.roleContainer,
                      )}
                    >
                      <CustomBadge
                        label={
                          getLabelFromOptions(USER_TYPE_OPTIONS, record.type) ||
                          ""
                        }
                        color={
                          record.type === USER_TYPES.ADMINISTRATOR
                            ? "light-blue-dust"
                            : record.type === USER_TYPES.COACH
                              ? "lightgreen"
                              : "lightblue"
                        }
                      />
                      {!isAdmin && !isCoach && record.spaceId[0] && (
                        <div className="d-flex align-items-center gap-2">
                          {record.roles.map((role) => {
                            return (
                              <Typhography>
                                {getLabelFromOptions(
                                  ROLES_SPORT_OPTIONS[
                                    record.spaceId[0]
                                      .type as any as keyof typeof ROLES_SPORT_OPTIONS
                                  ],
                                  role,
                                ) || ""}
                              </Typhography>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                },
              },

              Team: {
                key: "teams",
                customComponent: (value: ITeam<ISpace>[], record: any) => {
                  if (record.type === USER_TYPES.ADMINISTRATOR) return "";
                  const { items, remainingCount } =
                    summarizeArray<ITeam<ISpace>>(value);
                  return (
                    <div
                      className={`d-flex align-items-center gap-2 ${style.teamsContainer}`}
                    >
                      {items.map((team, index) => {
                        return (
                          <div key={index} className={style.teamContainer}>
                            {record.spaceId?.[0] && (
                              <TypeSpaceIcon type={record.spaceId[0].type} />
                            )}
                            <Typhography
                              size="medium-12"
                              className={"color-secondary-1 white-space-nowrap"}
                            >
                              {team.name}
                            </Typhography>
                            <Typhography
                              size="regular-12"
                              className={"color-secondary-1 white-space-nowrap"}
                            >
                              {record.spaceId?.[0] &&
                                getLabelFromOptions(
                                  (LEVELS_SPORT_OPTIONS as any)[
                                    record.spaceId[0].type
                                  ],
                                  team.level,
                                )}
                            </Typhography>
                          </div>
                        );
                      })}
                      {Boolean(remainingCount) && (
                        <div className={style.remainingCountContainer}>
                          <Typhography
                            size="medium-12"
                            className={"color-secondary-1"}
                          >
                            +{remainingCount}
                          </Typhography>
                        </div>
                      )}
                    </div>
                  );
                },
              },
            }}
          />
        </div>
        {data && (
          <div className="d-flex justify-content-end align-items-center">
            <select
              style={{
                width: "max-content",
              }}
              className="me-4"
              value={pagination.limit}
              onChange={({ target: { value } }) => {
                updatePagination("limit", +value);
              }}
            >
              <option value="1">1</option>
              <option value="20">20</option>
              <option value="50">50</option>
            </select>
            <CustomPaginationTable
              data={data!}
              onChange={(value) => {
                updatePagination("page", value);
              }}
            />
          </div>
        )}
      </div>
      <AddUserModal isOpen={isOpen} onClose={onClose} fetch={fetch} />
    </>
  );
};

export default UsersPage;
